import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Power,
  Phone,
  CreditCard,
  Database,
  Bell,
  Shield,
  Save,
  TestTube,
  ExternalLink,
  User,
  Key,
  RefreshCw,
  Loader2,
  Trash2,
  Sparkles,
} from "lucide-react";
import toast from "react-hot-toast";
import { useAuthStore } from "../stores/authStore";
import { ACTION_KEYS } from "../constants/actions";

export function Settings() {
  const { user, updateProfile, changePassword, hasPermission } = useAuthStore(
    (state) => ({
      user: state.user,
      updateProfile: state.updateProfile,
      changePassword: state.changePassword,
      hasPermission: state.hasPermission,
    })
  );
  const canManageSettings = hasPermission(ACTION_KEYS.SETTINGS_MANAGE);

  const [killSwitchEnabled, setKillSwitchEnabled] = useState(false);
  const [killSwitchReason, setKillSwitchReason] = useState("");
  const [billingRate, setBillingRate] = useState("1.00");
  const [timezone, setTimezone] = useState("America/Los_Angeles");
  const [isSaving, setIsSaving] = useState(false);

  // Schedule AI config
  const [aiProvider, setAiProvider] = useState("openai");
  const [aiModel, setAiModel] = useState("gpt-4o-mini");
  const [aiSystemPrompt, setAiSystemPrompt] = useState("");
  const [isSavingAi, setIsSavingAi] = useState(false);

  const [displayName, setDisplayName] = useState(
    user?.displayName || user?.email?.split("@")[0] || ""
  );
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [syncStatus, setSyncStatus] = useState<{
    isRunning: boolean;
    lastRunAt?: string | null;
    nextRunAt?: string | null;
  } | null>(null);
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncRunning, setSyncRunning] = useState(false);
  const [resettingCache, setResettingCache] = useState(false);

  useEffect(() => {
    setDisplayName(user?.displayName || user?.email?.split("@")[0] || "");
  }, [user?.displayName, user?.email]);

  const loadSyncStatus = async () => {
    setSyncLoading(true);
    try {
      const response = await fetch("/api/v1/telephony-sync/status");
      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        throw new Error(
          errorBody?.error || "Failed to load telephony sync status"
        );
      }
      const payload = await response.json();
      setSyncStatus(payload?.data || null);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to load sync status";
      toast.error(message);
    } finally {
      setSyncLoading(false);
    }
  };

  useEffect(() => {
    if (canManageSettings) {
      void loadSyncStatus();
      void fetch("/api/v1/app-settings/schedule-ai", {
        headers: { Authorization: `Bearer ${localStorage.getItem("token") ?? ""}` },
      })
        .then(r => r.json())
        .then(d => {
          if (d.config) {
            setAiProvider(d.config.provider ?? "openai");
            setAiModel(d.config.model ?? "gpt-4o-mini");
            setAiSystemPrompt(d.config.systemPrompt ?? "");
          }
        })
        .catch(() => {});
    }
  }, [canManageSettings]);

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);

  const saveSettings = async () => {
    setIsSaving(true);
    try {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1000));
      toast.success("Settings saved successfully");
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setIsSaving(false);
    }
  };

  const toggleKillSwitch = async () => {
    try {
      setKillSwitchEnabled(!killSwitchEnabled);
      if (!killSwitchEnabled) {
        toast.success(
          "Kill switch activated - all billing jobs are now paused"
        );
      } else {
        toast.success("Kill switch deactivated - billing jobs can now run");
      }
    } catch (error) {
      toast.error("Failed to toggle kill switch");
    }
  };

  const testPhoneAPI = async () => {
    try {
      toast.success("Phone API connection test successful");
    } catch (error) {
      toast.error("Phone API connection test failed");
    }
  };

  const testQBOAPI = async () => {
    try {
      toast.success("QBO API connection test successful");
    } catch (error) {
      toast.error("QBO API connection test failed");
    }
  };

  const submitProfile = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmed = displayName.trim();
    if (!trimmed) {
      toast.error("Display name cannot be empty");
      return;
    }

    setIsSavingProfile(true);
    try {
      await updateProfile(trimmed);
      toast.success("Profile updated");
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to update profile";
      toast.error(message);
    } finally {
      setIsSavingProfile(false);
    }
  };

  const submitPassword = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!currentPassword) {
      toast.error("Current password is required");
      return;
    }

    if (newPassword.length < 8) {
      toast.error("New password must be at least 8 characters");
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error("New password and confirmation do not match");
      return;
    }

    setIsUpdatingPassword(true);
    try {
      await changePassword(currentPassword, newPassword);
      toast.success("Password updated");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to update password";
      toast.error(message);
    } finally {
      setIsUpdatingPassword(false);
    }
  };

  const handleRunSync = async () => {
    setSyncRunning(true);
    try {
      const response = await fetch("/api/v1/telephony-sync/run", {
        method: "POST",
      });
      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        throw new Error(
          errorBody?.error || "Failed to start telephony usage sync"
        );
      }
      toast.success("Telephony usage sync started");
      await loadSyncStatus();
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to start sync";
      toast.error(message);
    } finally {
      setSyncRunning(false);
    }
  };

  const handleResetCache = async () => {
    const confirmed = window.confirm(
      "This will delete all cached telephony usage data and coverage ranges, then re-sync the last 8 weeks. Continue?"
    );
    if (!confirmed) return;

    setResettingCache(true);
    try {
      const response = await fetch("/api/v1/telephony-sync/reset", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ all: true, resync: true }),
      });
      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        throw new Error(
          errorBody?.error || "Failed to reset telephony usage cache"
        );
      }
      toast.success("Telephony cache reset; resync started");
      await loadSyncStatus();
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to reset cache";
      toast.error(message);
    } finally {
      setResettingCache(false);
    }
  };

  const formatTimestamp = (value?: string | null) =>
    value ? new Date(value).toLocaleString() : "Not yet run";

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
        <p className="text-gray-600">
          Configure system settings and integrations
        </p>
      </div>

      {canManageSettings && (
        <div className="card p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="p-2 bg-indigo-100 rounded-lg">
                <RefreshCw className="h-6 w-6 text-indigo-600" />
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Telephony Data Sync
                </h3>
                <p className="text-sm text-gray-500">
                  Pull missing usage for the last 8 weeks and refresh cached
                  projections.
                </p>
              </div>
            </div>
            <div className="flex flex-col md:flex-row md:items-center md:space-x-2 space-y-2 md:space-y-0">
              <button
                type="button"
                onClick={handleRunSync}
                disabled={syncRunning || syncStatus?.isRunning || resettingCache}
                className="btn btn-primary btn-md inline-flex items-center"
              >
                {syncRunning || syncStatus?.isRunning ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Syncing...
                  </>
                ) : (
                  "Run sync now"
                )}
              </button>
              <button
                type="button"
                onClick={handleResetCache}
                disabled={resettingCache || syncRunning || syncStatus?.isRunning}
                className="btn btn-danger btn-md inline-flex items-center"
              >
                {resettingCache ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Resetting...
                  </>
                ) : (
                  <>
                    <Trash2 className="h-4 w-4 mr-2" />
                    Reset cache & resync
                  </>
                )}
              </button>
            </div>
          </div>
          <p className="mt-3 text-xs text-red-600">
            Reset cache clears all telephony usage rows and coverage ranges, then
            re-syncs the last 8 weeks from the provider.
          </p>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600">
            <div className="flex flex-col">
              <span className="text-xs uppercase text-gray-400">Status</span>
              <span className="font-medium text-gray-900">
                {syncLoading
                  ? "Loading..."
                  : syncStatus?.isRunning
                  ? "Running"
                  : "Idle"}
              </span>
            </div>
            <div className="flex flex-col">
              <span className="text-xs uppercase text-gray-400">Last run</span>
              <span className="font-medium text-gray-900">
                {syncLoading ? "Loading..." : formatTimestamp(syncStatus?.lastRunAt)}
              </span>
            </div>
            <div className="flex flex-col">
              <span className="text-xs uppercase text-gray-400">Next scheduled</span>
              <span className="font-medium text-gray-900">
                {syncLoading ? "Loading..." : formatTimestamp(syncStatus?.nextRunAt)}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Account Management */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card p-6">
          <div className="flex items-center mb-4">
            <div className="p-2 bg-blue-100 rounded-lg">
              <User className="h-6 w-6 text-blue-600" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 ml-4">
              Profile Information
            </h3>
          </div>
          <form className="space-y-4" onSubmit={submitProfile}>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Display name
              </label>
              <input
                type="text"
                value={displayName}
                onChange={(event) => setDisplayName(event.target.value)}
                className="input mt-1"
                placeholder="Your name"
                required
              />
              <p className="text-xs text-gray-500 mt-2">
                This name is shown to teammates and appears on logs or audit
                trails.
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Account email
              </label>
              <input
                type="email"
                value={user?.email || ""}
                disabled
                className="input mt-1 bg-gray-100"
              />
            </div>
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={isSavingProfile}
                className="btn btn-primary btn-md inline-flex items-center"
              >
                <Save className="h-4 w-4 mr-2" />
                {isSavingProfile ? "Saving..." : "Save changes"}
              </button>
            </div>
          </form>
        </div>

        <div className="card p-6">
          <div className="flex items-center mb-4">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Key className="h-6 w-6 text-purple-600" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 ml-4">
              Change Password
            </h3>
          </div>
          <form className="space-y-4" onSubmit={submitPassword}>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Current password
              </label>
              <input
                type="password"
                autoComplete="current-password"
                value={currentPassword}
                onChange={(event) => setCurrentPassword(event.target.value)}
                className="input mt-1"
                placeholder="Enter current password"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                New password
              </label>
              <input
                type="password"
                autoComplete="new-password"
                value={newPassword}
                onChange={(event) => setNewPassword(event.target.value)}
                className="input mt-1"
                placeholder="At least 8 characters"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Confirm new password
              </label>
              <input
                type="password"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
                className="input mt-1"
                placeholder="Re-enter new password"
                required
              />
            </div>
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={isUpdatingPassword}
                className="btn btn-secondary btn-md"
              >
                {isUpdatingPassword ? "Updating..." : "Update password"}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Kill Switch */}
      <div className="card p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <div className="p-2 bg-danger-100 rounded-lg">
              <Power className="h-6 w-6 text-danger-600" />
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-medium text-gray-900">
                Emergency Kill Switch
              </h3>
              <p className="text-sm text-gray-600">
                Immediately stop all billing jobs and prevent new ones from
                starting
              </p>
            </div>
          </div>
          <button
            onClick={toggleKillSwitch}
            className={`btn btn-lg ${
              killSwitchEnabled ? "btn-danger" : "btn-success"
            }`}
          >
            {killSwitchEnabled ? "Deactivate" : "Activate"} Kill Switch
          </button>
        </div>
        {killSwitchEnabled && (
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700">
              Reason for Kill Switch
            </label>
            <input
              type="text"
              value={killSwitchReason}
              onChange={(e) => setKillSwitchReason(e.target.value)}
              placeholder="Enter reason for activating kill switch..."
              className="input mt-1"
            />
          </div>
        )}
      </div>

      {/* Billing Configuration */}
      <div className="card p-6">
        <div className="flex items-center mb-4">
          <div className="p-2 bg-primary-100 rounded-lg">
            <CreditCard className="h-6 w-6 text-primary-600" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 ml-4">
            Billing Configuration
          </h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Rate per Minute ($)
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={billingRate}
              onChange={(e) => setBillingRate(e.target.value)}
              className="input mt-1"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Billing Timezone
            </label>
            <select
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
              className="input mt-1"
            >
              <option value="America/Los_Angeles">
                Pacific Time (PST/PDT)
              </option>
              <option value="America/Denver">Mountain Time (MST/MDT)</option>
              <option value="America/Chicago">Central Time (CST/CDT)</option>
              <option value="America/New_York">Eastern Time (EST/EDT)</option>
              <option value="UTC">UTC</option>
            </select>
          </div>
        </div>
      </div>

      {/* API Integrations */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Phone System API */}
        <div className="card p-6">
          <div className="flex items-center mb-4">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Phone className="h-6 w-6 text-blue-600" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 ml-4">
              Phone System API
            </h3>
          </div>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Base URL
              </label>
              <input
                type="url"
                placeholder="https://api.mypurecloud.com"
                className="input mt-1"
                disabled
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                API Key
              </label>
              <input
                type="password"
                placeholder="••••••••••••••••"
                className="input mt-1"
                disabled
              />
            </div>
            <button
              onClick={testPhoneAPI}
              className="btn btn-secondary btn-md w-full"
            >
              <TestTube className="h-4 w-4 mr-2" />
              Test Connection
            </button>
          </div>
        </div>

        {/* QuickBooks Online API */}
        <div className="card p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <CreditCard className="h-6 w-6 text-green-600" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 ml-4">
                QuickBooks Online API
              </h3>
            </div>
            <Link to="/qbo-setup" className="btn btn-primary btn-sm">
              <ExternalLink className="h-4 w-4 mr-2" />
              Setup
            </Link>
          </div>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Environment
              </label>
              <select className="input mt-1" disabled>
                <option value="sandbox">Sandbox</option>
                <option value="production">Production</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Client ID
              </label>
              <input
                type="text"
                placeholder="••••••••••••••••"
                className="input mt-1"
                disabled
              />
            </div>
            <button
              onClick={testQBOAPI}
              className="btn btn-secondary btn-md w-full"
            >
              <TestTube className="h-4 w-4 mr-2" />
              Test Connection
            </button>
          </div>
        </div>
      </div>

      {/* Database & Logging */}
      <div className="card p-6">
        <div className="flex items-center mb-4">
          <div className="p-2 bg-gray-100 rounded-lg">
            <Database className="h-6 w-6 text-gray-600" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 ml-4">
            Database & Logging
          </h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Database Host
            </label>
            <input
              type="text"
              value="localhost"
              className="input mt-1"
              disabled
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Database Name
            </label>
            <input
              type="text"
              value="phone_billing"
              className="input mt-1"
              disabled
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Log Level
            </label>
            <select className="input mt-1" disabled>
              <option value="error">Error</option>
              <option value="warn">Warning</option>
              <option value="info" selected>
                Info
              </option>
              <option value="debug">Debug</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Log Retention
            </label>
            <select className="input mt-1" disabled>
              <option value="7d">7 days</option>
              <option value="14d" selected>
                14 days
              </option>
              <option value="30d">30 days</option>
            </select>
          </div>
        </div>
      </div>

      {/* Notifications */}
      <div className="card p-6">
        <div className="flex items-center mb-4">
          <div className="p-2 bg-yellow-100 rounded-lg">
            <Bell className="h-6 w-6 text-yellow-600" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 ml-4">
            Notifications
          </h3>
        </div>
        <div className="space-y-4">
          <div className="flex items-center">
            <input
              type="checkbox"
              id="emailNotifications"
              defaultChecked
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            />
            <label
              htmlFor="emailNotifications"
              className="ml-2 block text-sm text-gray-900"
            >
              Email notifications for billing job completion/failure
            </label>
          </div>
          <div className="flex items-center">
            <input
              type="checkbox"
              id="slackNotifications"
              defaultChecked
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            />
            <label
              htmlFor="slackNotifications"
              className="ml-2 block text-sm text-gray-900"
            >
              Slack notifications for critical system events
            </label>
          </div>
        </div>
      </div>

      {/* Security */}
      <div className="card p-6">
        <div className="flex items-center mb-4">
          <div className="p-2 bg-red-100 rounded-lg">
            <Shield className="h-6 w-6 text-red-600" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 ml-4">Security</h3>
        </div>
        <div className="space-y-4">
          <div className="flex items-center">
            <input
              type="checkbox"
              id="rateLimiting"
              defaultChecked
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            />
            <label
              htmlFor="rateLimiting"
              className="ml-2 block text-sm text-gray-900"
            >
              Enable API rate limiting
            </label>
          </div>
          <div className="flex items-center">
            <input
              type="checkbox"
              id="auditLogging"
              defaultChecked
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            />
            <label
              htmlFor="auditLogging"
              className="ml-2 block text-sm text-gray-900"
            >
              Enable audit logging for all actions
            </label>
          </div>
        </div>
      </div>

      {/* Schedule AI Config */}
      {canManageSettings && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className="h-5 w-5 text-purple-500" />
            <h2 className="text-lg font-semibold text-gray-900">Schedule AI</h2>
          </div>
          <p className="text-sm text-gray-500 mb-4">
            Configure the AI used for "Enhance with AI" on the PCC Schedule page. The system prompt guides how the AI scores PCC-account pairs.
          </p>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Provider</label>
              <select
                value={aiProvider}
                onChange={e => setAiProvider(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                <option value="openai">OpenAI</option>
                <option value="anthropic">Anthropic</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Model</label>
              <input
                type="text"
                value={aiModel}
                onChange={e => setAiModel(e.target.value)}
                placeholder="gpt-4o-mini"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">System Prompt</label>
            <textarea
              value={aiSystemPrompt}
              onChange={e => setAiSystemPrompt(e.target.value)}
              rows={6}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-purple-500"
              placeholder="Instructions for the AI scheduler..."
            />
          </div>
          <div className="flex justify-end">
            <button
              disabled={isSavingAi}
              onClick={async () => {
                setIsSavingAi(true);
                try {
                  const res = await fetch("/api/v1/app-settings/schedule-ai", {
                    method: "PATCH",
                    headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token") ?? ""}` },
                    body: JSON.stringify({ provider: aiProvider, model: aiModel, systemPrompt: aiSystemPrompt }),
                  });
                  if (!res.ok) throw new Error();
                  toast.success("AI settings saved");
                } catch {
                  toast.error("Failed to save AI settings");
                } finally {
                  setIsSavingAi(false);
                }
              }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 disabled:opacity-50"
            >
              <Save className="h-4 w-4" />
              {isSavingAi ? "Saving..." : "Save AI Settings"}
            </button>
          </div>
        </div>
      )}

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          onClick={saveSettings}
          disabled={isSaving}
          className="btn btn-primary btn-lg"
        >
          <Save className="h-4 w-4 mr-2" />
          {isSaving ? "Saving..." : "Save Settings"}
        </button>
      </div>
    </div>
  );
}
